/**
 * ReliefYou product page core.
 * Owner: Product agent. Markup: sections/ry-main-product.liquid, snippets/ry-buybox.liquid. Styles: assets/ry-product.css.
 *
 * Custom elements
 * - <ry-product-gallery>: scroll-snap media gallery with thumbnail buttons (roving tabindex, arrow keys, Home/End)
 *   and a "1 / 6" counter on mobile, where the thumbnails are hidden.
 * - <ry-buybox>: purchase options, quantity, prices, auto-renewal disclosure and consent.
 *   A capture-phase "submit" listener on <ry-buybox> (an ancestor of Dawn's <product-form>) stops an
 *   unconsented subscription submit before Dawn's product-form ever sees it. The consent box is unchecked
 *   whenever the disclosure changes, so consent always matches the terms on screen.
 * - <ry-sticky-atc>: bar that appears once the add-to-cart button is out of view (on mobile also while it is
 *   still below the fold; on desktop only after scrolling past it). Its button only scrolls to and focuses
 *   the purchase options; it never adds anything to the cart.
 * - The coming-soon notify form's messages are handled for every customer form by the inline script in
 *   layout/theme.liquid (it has to run on every page, before autofocus).
 * - Gallery: the optional Chewy sticker lives inside the first slide, so it scrolls away with it.
 *
 * Events (on document)
 * - ry:buybox-change  detail: { sectionId, type: 'sub' | 'once', planId, quantity, total, modeLabel, available }
 * - ry:variant-change detail: { sectionId, variantId, mediaId }
 */
(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const scrollBehavior = () => (reducedMotion.matches ? 'auto' : 'smooth');
  const fill = (template, tokens) =>
    Object.keys(tokens).reduce((text, key) => text.split(`[${key}]`).join(tokens[key]), template || '');
  const normalize = (text) => (text || '').replace(/\s+/g, ' ').trim();

  /* ------------------------------------------------------------------------
     Gallery
     ------------------------------------------------------------------------ */
  if (!customElements.get('ry-product-gallery')) {
    class RyProductGallery extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-ry-gallery-track]');
        this.slides = Array.from(this.querySelectorAll('[data-ry-slide]'));
        this.thumbs = Array.from(this.querySelectorAll('[data-ry-thumb]'));
        this.thumbList = this.querySelector('[data-ry-thumbs]');
        this.countCurrent = this.querySelector('[data-ry-count-current]');
        if (!this.track || this.slides.length < 2) return;

        this.activeIndex = Math.max(
          0,
          this.thumbs.findIndex((thumb) => thumb.getAttribute('aria-current') === 'true')
        );

        this.onThumbClick = this.onThumbClick.bind(this);
        this.onThumbKeydown = this.onThumbKeydown.bind(this);
        this.onVariantChange = this.onVariantChange.bind(this);

        this.thumbs.forEach((thumb) => {
          thumb.addEventListener('click', this.onThumbClick);
          thumb.addEventListener('keydown', this.onThumbKeydown);
        });

        if ('IntersectionObserver' in window) {
          this.observer = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (entry.isIntersecting) this.setActive(this.slides.indexOf(entry.target));
              });
            },
            { root: this.track, threshold: 0.6 }
          );
          this.slides.forEach((slide) => this.observer.observe(slide));
        }

        document.addEventListener('ry:variant-change', this.onVariantChange);
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        document.removeEventListener('ry:variant-change', this.onVariantChange);
      }

      goTo(index, focusThumb = false) {
        const target = Math.min(Math.max(index, 0), this.slides.length - 1);
        const slide = this.slides[target];
        this.track.scrollTo({ left: slide.offsetLeft, behavior: scrollBehavior() });
        this.setActive(target);
        if (focusThumb && this.thumbs[target]) this.thumbs[target].focus();
      }

      setActive(index) {
        if (index < 0) return;
        this.activeIndex = index;
        if (this.countCurrent) this.countCurrent.textContent = String(index + 1);
        this.thumbs.forEach((thumb, i) => {
          const current = i === index;
          thumb.setAttribute('aria-current', current ? 'true' : 'false');
          thumb.tabIndex = current ? 0 : -1;
        });
        this.slides.forEach((slide, i) => {
          if (i === index) return;
          const video = slide.querySelector('video');
          if (video && !video.paused) video.pause();
        });
        this.revealThumb(this.thumbs[index]);
      }

      /* Keep the active thumbnail visible inside the thumbnail strip without scrolling the page. */
      revealThumb(thumb) {
        if (!thumb || !this.thumbList) return;
        // The list is position: relative, so offsetLeft/offsetTop are relative to it.
        const list = this.thumbList;
        const item = thumb.parentElement;
        if (list.scrollWidth > list.clientWidth) {
          const left = item.offsetLeft;
          if (left < list.scrollLeft || left + item.offsetWidth > list.scrollLeft + list.clientWidth) {
            list.scrollTo({ left: Math.max(left - 8, 0), behavior: scrollBehavior() });
          }
        } else if (list.scrollHeight > list.clientHeight) {
          const top = item.offsetTop;
          if (top < list.scrollTop || top + item.offsetHeight > list.scrollTop + list.clientHeight) {
            list.scrollTo({ top: Math.max(top - 8, 0), behavior: scrollBehavior() });
          }
        }
      }

      onThumbClick(event) {
        this.goTo(Number(event.currentTarget.dataset.index));
      }

      onThumbKeydown(event) {
        const index = Number(event.currentTarget.dataset.index);
        const last = this.thumbs.length - 1;
        let next = null;
        switch (event.key) {
          case 'ArrowRight':
          case 'ArrowDown':
            next = index === last ? 0 : index + 1;
            break;
          case 'ArrowLeft':
          case 'ArrowUp':
            next = index === 0 ? last : index - 1;
            break;
          case 'Home':
            next = 0;
            break;
          case 'End':
            next = last;
            break;
          default:
            return;
        }
        event.preventDefault();
        this.goTo(next, true);
      }

      onVariantChange(event) {
        const { sectionId, mediaId } = event.detail || {};
        if (sectionId !== this.dataset.section || !mediaId) return;
        const index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
        if (index > -1) this.goTo(index);
      }
    }

    customElements.define('ry-product-gallery', RyProductGallery);
  }

  /* ------------------------------------------------------------------------
     Buy box
     ------------------------------------------------------------------------ */
  if (!customElements.get('ry-buybox')) {
    class RyBuybox extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form[data-type="add-to-cart-form"]');
        if (!this.form) return;

        try {
          const json = this.querySelector('[data-ry-buybox-data]');
          this.data = json ? JSON.parse(json.textContent) : {};
        } catch (error) {
          this.data = {};
        }
        this.labels = this.data.labels || {};
        this.maxQuantity = Number(this.data.maxQuantity) || 1;

        this.planInputs = Array.from(this.querySelectorAll('[data-ry-plan-input]'));
        this.cards = Array.from(this.querySelectorAll('[data-ry-plan-card]'));
        this.variantSelect = this.querySelector('[data-ry-variant]');
        this.variantInput = this.form.querySelector('[name="id"]');
        this.qtyInput = this.querySelector('[data-ry-qty-input]');
        this.qtyButtons = Array.from(this.querySelectorAll('[data-ry-qty]'));
        this.arl = this.querySelector('[data-ry-arl]');
        this.consent = this.querySelector('[data-ry-consent]');
        this.consentError = this.querySelector('[data-ry-consent-error]');
        this.disclosureText = this.querySelector('[data-ry-disclosure-text]');
        this.arlFields = Array.from(this.querySelectorAll('[data-ry-arl-field]'));
        this.arlDisclosureField = this.querySelector('[data-ry-arl-disclosure]');
        this.arlTimeField = this.querySelector('[data-ry-arl-time]');
        this.submitButton = this.querySelector('[data-ry-submit]');
        this.submitLabel = this.querySelector('[data-ry-submit-label]');
        this.renew = this.querySelector('[data-ry-renew]');
        this.live = this.querySelector('[data-ry-live]');

        // JS owns validation from here on (the server-rendered form validates natively as a fallback).
        this.form.noValidate = true;

        // Subscription options are rendered disabled when one-time is pre-selected, so a page without
        // working JS can only add one-time purchases. update() then disables options a variant doesn't offer.
        this.planInputs.forEach((planInput) => {
          if (planInput.hasAttribute('data-ry-nojs-disabled')) {
            planInput.disabled = false;
            planInput.removeAttribute('data-ry-nojs-disabled');
          }
        });

        // Consent is never pre-checked, also not by browser form-state restoration.
        if (this.consent) this.consent.checked = false;
        this.wasSub = false;

        this.onSubmitCapture = this.onSubmitCapture.bind(this);
        this.onChange = this.onChange.bind(this);
        this.onClick = this.onClick.bind(this);
        this.onQtyInput = this.onQtyInput.bind(this);
        this.onFocusIn = this.onFocusIn.bind(this);
        this.onFocusOut = this.onFocusOut.bind(this);

        // Capture phase on an ancestor: runs before <product-form>'s own submit listener on the form.
        this.addEventListener('submit', this.onSubmitCapture, true);
        this.addEventListener('change', this.onChange);
        this.addEventListener('click', this.onClick);
        this.addEventListener('focusin', this.onFocusIn);
        this.addEventListener('focusout', this.onFocusOut);
        if (this.qtyInput) this.qtyInput.addEventListener('input', this.onQtyInput);

        // Back/forward cache restores the page with the box still ticked: reset it.
        this.onPageShow = (event) => {
          if (!event.persisted) return;
          if (this.consent) this.consent.checked = false;
          this.update(false);
        };
        window.addEventListener('pageshow', this.onPageShow);

        this.update(false);
      }

      disconnectedCallback() {
        this.removeEventListener('submit', this.onSubmitCapture, true);
        if (this.onPageShow) window.removeEventListener('pageshow', this.onPageShow);
      }

      get sectionId() {
        return this.dataset.section;
      }

      get variantId() {
        return this.variantInput ? String(this.variantInput.value) : '';
      }

      get variantData() {
        const variants = this.data.variants || {};
        return variants[this.variantId] || null;
      }

      get selectedInput() {
        return this.planInputs.find((input) => input.checked) || null;
      }

      get isSubscription() {
        const input = this.selectedInput;
        return Boolean(input && input.dataset.type === 'sub' && input.value);
      }

      get quantity() {
        if (!this.qtyInput) return 1;
        const value = parseInt(this.qtyInput.value, 10);
        if (Number.isNaN(value)) return 1;
        return Math.min(Math.max(value, 1), this.maxQuantity);
      }

      get optionKey() {
        return this.isSubscription ? String(this.selectedInput.value) : 'once';
      }

      /* Current state, also read by <ry-sticky-atc> when it connects. */
      get state() {
        if (!this.form) return null;
        const input = this.selectedInput;
        const variant = this.variantData;
        const option = variant && variant.options ? variant.options[this.optionKey] : null;
        const quantity = this.quantity;
        return {
          sectionId: this.sectionId,
          type: this.isSubscription ? 'sub' : 'once',
          planId: this.isSubscription ? String(input.value) : '',
          quantity,
          total: option && option.totals ? option.totals[quantity - 1] || option.price : '',
          modeLabel: input ? input.dataset.modeLabel || '' : '',
          available: Boolean(variant && variant.available),
        };
      }

      onChange(event) {
        const target = event.target;
        if (target === this.variantSelect) {
          this.onVariantChange();
          return;
        }
        if (target.matches('[data-ry-plan-input]')) {
          this.hideConsentError();
          this.update(true);
          return;
        }
        if (target === this.qtyInput) {
          this.qtyInput.value = this.quantity;
          this.update(true);
          return;
        }
        if (target === this.consent && this.consent.checked) this.hideConsentError();
      }

      onQtyInput() {
        if (this.qtyInput.value === '') return;
        this.update(false);
      }

      onClick(event) {
        const button = event.target.closest('[data-ry-qty]');
        if (!button || !this.qtyInput) return;
        event.preventDefault();
        if (button.getAttribute('aria-disabled') === 'true') return;
        const step = button.dataset.ryQty === 'plus' ? 1 : -1;
        const next = Math.min(Math.max(this.quantity + step, 1), this.maxQuantity);
        if (next === this.quantity) return;
        this.qtyInput.value = next;
        this.update(true);
      }

      onFocusIn(event) {
        if (!event.target.matches('[data-ry-plan-input]')) return;
        let visible = true;
        try {
          visible = event.target.matches(':focus-visible');
        } catch (error) {
          visible = true;
        }
        const card = event.target.closest('[data-ry-plan-card]');
        if (card) card.classList.toggle('is-focused', visible);
      }

      onFocusOut(event) {
        if (!event.target.matches('[data-ry-plan-input]')) return;
        const card = event.target.closest('[data-ry-plan-card]');
        if (card) card.classList.remove('is-focused');
      }

      onVariantChange() {
        const variant = this.variantData;
        this.update(true);
        if (window.history && window.history.replaceState && this.variantId) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', this.variantId);
          window.history.replaceState({}, '', url.toString());
        }
        document.dispatchEvent(
          new CustomEvent('ry:variant-change', {
            detail: {
              sectionId: this.sectionId,
              variantId: this.variantId,
              mediaId: variant ? variant.mediaId : null,
            },
          })
        );
      }

      /* ---- Submit guard ------------------------------------------------ */
      onSubmitCapture(event) {
        if (event.target !== this.form) return;
        if (this.qtyInput) this.qtyInput.value = this.quantity;

        // Decide on what is actually posted: an app could inject its own selling_plan input.
        const postedPlan = new FormData(this.form).getAll('selling_plan').some(Boolean);
        const isSub = this.isSubscription || postedPlan;

        if (!isSub) {
          this.setArlFieldsEnabled(false);
          return;
        }

        if (!this.isSubscription && this.arl) {
          // A plan is posted that this buy box didn't select: show the terms and require consent.
          this.arl.hidden = false;
          if (this.consent) {
            this.consent.disabled = false;
            this.consent.required = true;
          }
        }

        if (!this.consent || !this.consent.checked) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.showConsentError();
          return;
        }

        // Record consent on the cart line: the exact disclosure shown, and when it was accepted.
        this.setArlFieldsEnabled(true);
        if (this.arlDisclosureField) this.arlDisclosureField.value = this.currentDisclosure();
        if (this.arlTimeField) {
          this.arlTimeField.disabled = false;
          this.arlTimeField.value = new Date().toISOString();
        }
      }

      currentDisclosure() {
        return this.disclosureText ? normalize(this.disclosureText.textContent) : '';
      }

      setArlFieldsEnabled(enabled) {
        this.arlFields.forEach((field) => {
          if (field === this.arlTimeField && enabled) return;
          field.disabled = !enabled;
        });
      }

      showConsentError() {
        if (!this.consent) return;
        const describedBy = [this.dataset.disclosureId];
        if (this.consentError) {
          this.consentError.hidden = false;
          describedBy.push(this.dataset.errorId);
        }
        this.consent.setAttribute('aria-invalid', 'true');
        this.consent.setAttribute('aria-describedby', describedBy.filter(Boolean).join(' '));
        this.consent.focus();
        if (typeof this.consent.scrollIntoView === 'function') {
          this.consent.scrollIntoView({ block: 'center', behavior: scrollBehavior() });
        }
      }

      hideConsentError() {
        if (!this.consent) return;
        if (this.consentError) this.consentError.hidden = true;
        this.consent.removeAttribute('aria-invalid');
        if (this.dataset.disclosureId) this.consent.setAttribute('aria-describedby', this.dataset.disclosureId);
      }

      /* ---- Rendering --------------------------------------------------- */
      update(announce) {
        const variant = this.variantData;

        // Options that the current variant does not offer are hidden; fall back to one that it does.
        if (variant && variant.options) {
          this.cards.forEach((card) => {
            const radio = card.querySelector('[data-ry-plan-input]');
            if (!radio) return;
            const offered = Boolean(variant.options[card.dataset.ryOption]);
            card.hidden = !offered;
            radio.disabled = !offered;
          });
          const selected = this.selectedInput;
          if (selected && selected.disabled) {
            const fallback = this.planInputs.find((planInput) => !planInput.disabled);
            if (fallback) fallback.checked = true;
          }
        }

        const isSub = this.isSubscription;
        const quantity = this.quantity;
        const input = this.selectedInput;
        const index = quantity - 1;

        // Cards: selected state and prices for the current variant.
        this.cards.forEach((card) => {
          const radio = card.querySelector('[data-ry-plan-input]');
          if (radio) card.classList.toggle('is-selected', radio.checked);
          const option = variant && variant.options ? variant.options[card.dataset.ryOption] : null;
          if (option) this.renderCardPrices(card, option);
        });

        // Quantity buttons.
        this.qtyButtons.forEach((button) => {
          const atLimit = button.dataset.ryQty === 'plus' ? quantity >= this.maxQuantity : quantity <= 1;
          button.setAttribute('aria-disabled', atLimit ? 'true' : 'false');
        });

        const option = variant && variant.options ? variant.options[this.optionKey] : null;
        const total = option && option.totals ? option.totals[index] || option.price : '';
        const renewTotal = option && option.renewTotals ? option.renewTotals[index] : total;
        const firstTotal = option && option.firstTotals ? option.firstTotals[index] : total;
        let consentReset = false;

        // Auto-renewal block.
        if (this.arl) {
          this.arl.hidden = !isSub;
          if (this.consent) {
            this.consent.required = isSub;
            this.consent.disabled = !isSub;
          }
          this.setArlFieldsEnabled(isSub);
          if (this.arlTimeField) this.arlTimeField.disabled = true;
          if (isSub && input && this.disclosureText) {
            const text = fill(input.dataset.disclosureTemplate, { price: renewTotal, first_price: firstTotal });
            const changed = normalize(this.disclosureText.textContent) !== normalize(text);
            if (changed) this.disclosureText.textContent = text;
            if (this.arlDisclosureField) this.arlDisclosureField.value = normalize(text);
            // Consent given to other terms (another plan, quantity or variant, or before switching away
            // from the subscription) no longer counts.
            if (this.consent && this.consent.checked && (changed || !this.wasSub)) {
              this.consent.checked = false;
              consentReset = true;
            }
          }
        }
        this.wasSub = isSub;

        // Button and the line under it.
        const available = Boolean(variant ? variant.available : this.submitButton && !this.submitButton.disabled);
        let buttonText;
        if (!variant) {
          buttonText = this.submitLabel ? this.submitLabel.textContent : '';
        } else if (!available) {
          buttonText = this.labels.soldOut || '';
        } else {
          const label = isSub ? this.labels.sub : this.labels.once;
          buttonText = `${label}${this.labels.separator || ' · '}${total}`;
        }
        if (this.submitLabel) this.submitLabel.textContent = buttonText;
        if (this.submitButton && variant) {
          if (available) {
            this.submitButton.removeAttribute('disabled');
          } else {
            this.submitButton.setAttribute('disabled', 'disabled');
          }
        }

        let renewText = '';
        if (this.renew && input) {
          renewText = fill(input.dataset.renewTemplate, { price: renewTotal, first_price: firstTotal });
          this.renew.textContent = renewText;
          this.renew.hidden = renewText === '';
        }

        if (announce && this.live) {
          let quantityText = '';
          if (this.qtyInput) {
            const row = this.qtyInput.closest('.ry-buybox__qty-row');
            const qtyLabel = row ? row.querySelector('label') : null;
            quantityText = `${qtyLabel ? normalize(qtyLabel.textContent) : 'Quantity'} ${quantity}`;
          }
          const resetText = consentReset ? this.labels.consentReset || '' : '';
          const message = [quantityText, buttonText, renewText, resetText].filter(Boolean).join('. ');
          this.live.textContent = '';
          window.setTimeout(() => {
            this.live.textContent = message;
          }, 60);
        }

        document.dispatchEvent(new CustomEvent('ry:buybox-change', { detail: this.state }));
      }

      renderCardPrices(card, option) {
        const price = card.querySelector('[data-ry-price]');
        if (price && option.price) price.textContent = option.price;

        const perDay = card.querySelector('[data-ry-per-day]');
        if (perDay && option.perDay) perDay.textContent = fill(this.labels.perDay || '[price]', { price: option.perDay });

        const compareWrap = card.querySelector('[data-ry-compare-wrap]');
        const compare = card.querySelector('[data-ry-compare]');
        if (compareWrap && compare) {
          compareWrap.hidden = !option.compare;
          if (option.compare) compare.textContent = option.compare;
          const saleLabel = card.querySelector('[data-ry-sale-label]');
          if (saleLabel) saleLabel.hidden = !option.compare;
        }

        const save = card.querySelector('[data-ry-save]');
        const band = card.querySelector('[data-ry-band]');
        if (save) {
          const percent = Number(option.savePercent) || 0;
          const label = percent > 0 ? fill(this.labels.save || '', { percent: String(percent) }) : '';
          save.textContent = label ? `${save.dataset.prefix || ''}${label}` : '';
          if (band) band.hidden = normalize(band.textContent) === '';
        }
      }
    }

    customElements.define('ry-buybox', RyBuybox);
  }

  /* ------------------------------------------------------------------------
     Sticky add-to-cart bar
     ------------------------------------------------------------------------ */
  if (!customElements.get('ry-sticky-atc')) {
    class RyStickyAtc extends HTMLElement {
      connectedCallback() {
        this.target = document.getElementById(this.dataset.target);
        this.focusTarget = document.getElementById(this.dataset.focus);
        if (!this.target || !('IntersectionObserver' in window)) return;

        this.button = this.querySelector('[data-ry-sticky-btn]');
        this.price = this.querySelector('[data-ry-sticky-price]');
        this.mode = this.querySelector('[data-ry-sticky-mode]');
        this.allowDesktop = this.dataset.desktop === 'true';
        this.desktop = window.matchMedia('(min-width: 990px)');
        this.targetOut = false;
        this.targetAbove = false;

        this.onBuyboxChange = this.onBuyboxChange.bind(this);
        this.onButtonClick = this.onButtonClick.bind(this);
        this.toggle = this.toggle.bind(this);

        this.hidden = false;
        this.inert = true;

        // Desktop: only after scrolling past the button. Mobile: whenever the button is out of view, so the
        // purchase action is reachable from the first screen.
        this.observer = new IntersectionObserver(([entry]) => {
          this.targetOut = !entry.isIntersecting;
          this.targetAbove = entry.boundingClientRect.top < 0;
          this.toggle();
        });
        this.observer.observe(this.target);

        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(() => this.setHeight());
          this.resizeObserver.observe(this);
        }

        if (this.button) this.button.addEventListener('click', this.onButtonClick);
        document.addEventListener('ry:buybox-change', this.onBuyboxChange);
        this.desktop.addEventListener('change', this.toggle);

        const buybox = document.querySelector(`ry-buybox[data-section="${this.dataset.section}"]`);
        if (buybox && buybox.state) this.render(buybox.state);
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        if (this.resizeObserver) this.resizeObserver.disconnect();
        document.removeEventListener('ry:buybox-change', this.onBuyboxChange);
        if (this.desktop) this.desktop.removeEventListener('change', this.toggle);
        document.documentElement.classList.remove('ry-sticky-atc-on');
      }

      setHeight() {
        document.documentElement.style.setProperty('--ry-sticky-atc-h', `${this.offsetHeight}px`);
      }

      toggle() {
        const isDesktop = this.desktop.matches;
        const pastTarget = this.targetOut && (this.targetAbove || !isDesktop);
        const show = pastTarget && (this.allowDesktop || !isDesktop);
        // Don't leave keyboard focus inside a bar that is about to become inert and invisible.
        if (!show && this.contains(document.activeElement) && this.focusTarget) {
          const next =
            this.focusTarget.querySelector('[data-ry-plan-input]:checked') ||
            this.focusTarget.querySelector('input:not([type="hidden"]):not([disabled]), button:not([disabled])') ||
            document.getElementById(this.dataset.target);
          if (next) next.focus({ preventScroll: true });
        }
        this.classList.toggle('is-visible', show);
        this.inert = !show;
        document.documentElement.classList.toggle('ry-sticky-atc-on', show);
        if (show) this.setHeight();
      }

      onBuyboxChange(event) {
        if (event.detail && event.detail.sectionId === this.dataset.section) this.render(event.detail);
      }

      render(state) {
        if (this.price && state.total) this.price.textContent = state.total;
        if (this.mode) this.mode.textContent = state.modeLabel || '';
      }

      onButtonClick(event) {
        if (!this.focusTarget) return;
        event.preventDefault();
        const focusable =
          this.focusTarget.querySelector('[data-ry-plan-input]:checked') ||
          this.focusTarget.querySelector('input:not([type="hidden"]):not([disabled]), button:not([disabled])') ||
          document.getElementById(this.dataset.target);
        this.focusTarget.scrollIntoView({ block: 'start', behavior: scrollBehavior() });
        if (focusable) focusable.focus({ preventScroll: true });
      }
    }

    customElements.define('ry-sticky-atc', RyStickyAtc);
  }

  /* ------------------------------------------------------------------------
     Links to a details block (e.g. #supplement-facts) open it
     ------------------------------------------------------------------------ */
  const openDetails = (hash) => {
    let id = '';
    try {
      id = decodeURIComponent((hash || '').slice(1));
    } catch (error) {
      return;
    }
    if (!id) return;
    const target = document.getElementById(id);
    if (target && target.matches('details.ry-product__detail')) target.open = true;
  };
  window.addEventListener('hashchange', () => openDetails(window.location.hash));
  // Also when the hash doesn't change (the same link clicked twice).
  document.addEventListener('click', (event) => {
    const link = event.target.closest && event.target.closest('a[href*="#"]');
    if (link && link.pathname === window.location.pathname) openDetails(link.hash);
  });
  openDetails(window.location.hash);

  /* ------------------------------------------------------------------------
     Theme editor: open a details block when it is selected
     ------------------------------------------------------------------------ */
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:block:select', (event) => {
      const details = event.target.closest('.ry-product__detail');
      if (details) details.open = true;
    });
  }
})();
